import { useState, useRef, useEffect } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const baseUrl = window.location.protocol + '//' + window.location.host;

  const services = [
    { id: 'ofis-temizligi', name: 'Ofis & İş Yeri Temizliği' },
    { id: 'tadilat-sonrasi', name: 'Tadilat Sonrası Temizlik' },
    { id: 'yangin-sonrasi', name: 'Yangın Sonrası Temizlik' },
    { id: 'dis-cephetemizligi', name: 'Dış Cephe & Cam Temizliği' }
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target as Node)) {
        setIsServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white shadow-lg relative z-50">
      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex justify-between items-center py-4">

          {/* LOGO alanı - responsive optimize */}
          <div className="flex items-center space-x-2 flex-shrink-0">
           <img 
             src="/images/bt_logo.png" 
             alt="Bektaş Temizlik" 
             className="h-12 sm:h-14 md:h-16 w-auto object-contain"
           />

           <div className="flex items-baseline space-x-1">
             <span className="text-lg sm:text-xl md:text-2xl font-bold text-black-600 whitespace-nowrap">
               BEKTAŞ
             </span>
             <span className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600 whitespace-nowrap">
               TEMİZLİK
             </span>
           </div>
          </div>

          {/* Desktop NAV */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8 flex-shrink min-w-0">

            <Link to="/" className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap">
              Anasayfa
            </Link>
            
            {/* Hizmetler dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="flex items-center text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap"
              >
                Hizmetler
                <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <div
                className={`absolute left-0 mt-2 w-64 lg:w-72 bg-white rounded-xl shadow-2xl overflow-hidden transition-all duration-300 origin-top transform ${
                  isServicesOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                }`}
              >
                <div className="py-2">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/hizmetler/${service.id}`}
                      onClick={() => setIsServicesOpen(false)}
                      className="block px-6 py-3 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors whitespace-nowrap"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <a href={`${baseUrl}/#hakkimizda`} className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap">
              Hakkımızda
            </a>
            <a href={`${baseUrl}/#referanslar`} className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap">
              Referanslar
            </a>

            <Link
              to={`${baseUrl}/#iletisim`}
              onClick={(e) => {
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  document.getElementById('iletisim')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium whitespace-nowrap"
            >
              İletişim
            </Link>
          </nav>

          {/* PHONE button */}
          <a 
            href="tel:+905327385437"
            className="hidden md:flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex-shrink-0"
          >
            <Phone size={18} />
            <span className="hidden lg:inline font-medium">0532 738 54 37</span>
          </a>
      
          {/* Mobile menu button */}
          <button
            className="md:hidden ml-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* MOBILE MENU */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 pt-2 pb-3 space-y-1">

            <Link to="/" className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors">Anasayfa</Link>

            {/* Mobile dropdown */}
            <div className="border-b border-gray-100 pb-1" ref={servicesRef}>
              <button 
                onClick={() => setIsServicesOpen(!isServicesOpen)}
                className="w-full flex items-center justify-between px-3 py-3 text-gray-700 hover:text-blue-600 transition-colors"
              >
                Hizmetler
                <ChevronDown className={`h-4 w-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
              </button>

              <div className={`overflow-hidden transition-all duration-300 ${isServicesOpen ? 'max-h-96' : 'max-h-0'}`}>
                <div className="pl-4 py-1 space-y-1">
                  {services.map((service) => (
                    <Link
                      key={service.id}
                      to={`/hizmetler/${service.id}`}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsServicesOpen(false);
                      }}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <a 
              href={`${baseUrl}/#hakkimizda`}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              Hakkımızda
            </a>

            <a 
              href={`${baseUrl}/#referanslar`}
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              Referanslar
            </a>

            <a 
              href="/#iletisim"
              className="block px-3 py-2 text-gray-700 hover:text-blue-600 transition-colors"
            >
              İletişim
            </a>

          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
